import {
  CarFront,
  ClipboardCheck,
  ConciergeBell,
  FileCheck2,
  Home,
  PackageCheck,
  ShipWheel,
} from "lucide-react";

export const services = [
  {
    slug: "automobile-services",
    cardTitle: "Automobile Services",
    shortTitle: "Automobiles",
    title: "Find, verify and receive your vehicle with confidence",
    navTitle: "Automobile services",
    image: "https://images.pexels.com/photos/12555013/pexels-photo-12555013.jpeg?auto=compress&cs=tinysrgb&w=1600",
    icon: CarFront,
    accent: "ocean",
    description:
      "From a trusted vehicle search to the keys in your hands, our operations team coordinates sourcing, verification, negotiation, shipping and delivery across borders.",
    bullets: [
      "Vehicle sourcing from the US, UK, Canada and Europe",
      "Vehicle history checks, seller verification and pre-purchase inspection coordination",
      "Price negotiation and purchase documentation support",
      "Shipping, customs clearance, port release and inland delivery",
      "Registration handover and a complete delivery documentation checklist",
    ],
    workflow: [
      "Share your specification and budget",
      "Review verified options, inspection notes and landed-cost estimates",
      "Approve the vehicle, fund agreed milestones and receive progress updates",
      "Take delivery with the supporting documents and handover record",
    ],
  },
  {
    slug: "logistics",
    cardTitle: "Logistics",
    shortTitle: "Logistics",
    title: "End-to-end logistics with visibility at every handoff",
    navTitle: "Logistics",
    image: "https://images.pexels.com/photos/38941376/pexels-photo-38941376.jpeg?auto=compress&cs=tinysrgb&w=1600",
    icon: PackageCheck,
    accent: "cyan",
    description:
      "A single accountable team for freight planning, cargo consolidation, warehousing, domestic delivery and clear shipment visibility, whether goods are moving within Nigeria or across borders.",
    bullets: [
      "Air, sea and road freight forwarding with route planning",
      "Cargo consolidation, warehousing and inventory handoff",
      "Customs handoff, domestic transport and last-mile delivery",
      "Shipment tracking, exception follow-up and delivery confirmation",
      "Proof of delivery and a clear record of each operational handoff",
    ],
    workflow: [
      "Submit your shipment details",
      "Receive a clear route, scope, price and timeline",
      "Track each handoff and resolve exceptions with our operations team",
      "Confirm delivery and receive the completion record",
    ],
  },
  {
    slug: "clearing-and-forwarding",
    cardTitle: "Clearing and Forwarding",
    shortTitle: "Clearing & forwarding",
    title: "Clear the complexity at every border",
    navTitle: "Clearing & forwarding",
    image: "https://images.pexels.com/photos/34060583/pexels-photo-34060583.jpeg?auto=compress&cs=tinysrgb&w=1600",
    icon: ShipWheel,
    accent: "bridge",
    description:
      "We coordinate the documents, declarations and people involved in moving cargo through Nigerian ports and borders, with clear updates from pre-arrival review to final release.",
    bullets: [
      "Import and export document review and preparation",
      "Duty, tariff and shipment classification assessment support",
      "Customs declaration and agency submission coordination",
      "Port, terminal and examination scheduling follow-up",
      "Release, transport handover and clearance evidence",
    ],
    workflow: [
      "We review your cargo and documents",
      "Our team maps the clearance path, costs and dependencies",
      "We coordinate submissions, inspections and port stakeholders",
      "You receive progress updates, release evidence and handover details",
    ],
  },
  {
    slug: "real-estate-services",
    cardTitle: "Real Estate Services",
    shortTitle: "Real estate",
    title: "Make property decisions with more certainty",
    navTitle: "Real estate services",
    image: "https://images.pexels.com/photos/7489096/pexels-photo-7489096.jpeg?auto=compress&cs=tinysrgb&w=1600",
    icon: Home,
    accent: "ocean",
    description:
      "Whether you are buying from abroad, selling an asset or managing a project locally, we bring structured due diligence and on-ground accountability to property decisions.",
    bullets: [
      "Property sourcing against your location, budget and use case",
      "Title, deed, survey and ownership verification coordination",
      "Physical inspections, market checks and opportunity reports",
      "Transaction support, construction supervision and snag follow-up",
      "Property management, maintenance coordination and reporting",
    ],
    workflow: [
      "Define your location, budget and use case",
      "Review sourced opportunities, inspection notes and due diligence reports",
      "Agree the next action and keep decisions documented",
      "Proceed with confidence, or keep searching with better information",
    ],
  },
  {
    slug: "concierge-services",
    cardTitle: "Concierge Services",
    shortTitle: "Concierge",
    title: "A capable local hand for the details that matter",
    navTitle: "Concierge services",
    image: "https://images.pexels.com/photos/5467576/pexels-photo-5467576.jpeg?auto=compress&cs=tinysrgb&w=1600",
    icon: ConciergeBell,
    accent: "bridge",
    description:
      "From airport pickup to event support, our team handles practical details for residents, visitors and diaspora customers so you can focus on why you are here.",
    bullets: [
      "Airport pickup, transfers and accommodation arrangements",
      "Errand management, shopping and local appointment support",
      "Household, relocation and arrival assistance",
      "Event, hospitality and VIP guest coordination",
      "Completion updates, receipts and service evidence",
    ],
    workflow: [
      "Tell us what success looks like",
      "Receive the plan, price, timeline and any decisions we need from you",
      "Stay informed while the task is coordinated locally",
      "Receive confirmation, receipts and completion evidence",
    ],
  },
  {
    slug: "procurement-management",
    cardTitle: "Procurement Management as a Service (PMaaS)",
    shortTitle: "Procurement",
    title: "Procurement management that keeps buying accountable",
    navTitle: "Procurement management (PMaaS)",
    image: "https://images.pexels.com/photos/7018655/pexels-photo-7018655.jpeg?auto=compress&cs=tinysrgb&w=1600",
    icon: ClipboardCheck,
    accent: "cyan",
    description:
      "A disciplined extension of your team for requirement definition, supplier verification, competitive quotations, negotiation, purchasing and delivery, with a documented close-out for every supplier issue.",
    bullets: [
      "Requirements & Specifications: define requirements, specifications, quantities, budgets and approval criteria",
      "Supplier Sourcing & Verification: identify, shortlist and conduct basic due diligence on suitable suppliers",
      "Quotations & Negotiation: manage RFQs, compare supplier offers and negotiate pricing and commercial terms",
      "Purchase & Delivery Coordination: coordinate purchasing, supplier communications, order tracking and delivery",
      "Supplier Management & Issue Resolution: monitor performance, escalate issues and close out procurement activities with a clear record",
    ],
    workflow: [
      {
        title: "Requirements & Specifications",
        description:
          "Define procurement requirements, specifications, quantities, budgets and approval criteria.",
      },
      {
        title: "Supplier Sourcing & Verification",
        description:
          "Identify, shortlist and conduct basic due diligence on suitable suppliers.",
      },
      {
        title: "Quotations & Negotiation",
        description:
          "Manage RFQs, compare supplier offers and negotiate pricing and commercial terms.",
      },
      {
        title: "Purchase & Delivery Coordination",
        description:
          "Coordinate purchasing, supplier communications, order tracking and delivery.",
      },
      {
        title: "Supplier Management & Issue Resolution",
        description:
          "Monitor supplier performance, escalate issues and ensure procurement activities are properly closed out and documented.",
      },
    ],
  },
  {
    slug: "regulatory-compliance",
    cardTitle: "Regulatory Compliance Support",
    shortTitle: "Compliance",
    title: "Regulatory confidence for growing organisations",
    navTitle: "Regulatory compliance",
    image: "https://images.pexels.com/photos/30677714/pexels-photo-30677714.jpeg?auto=compress&cs=tinysrgb&w=1600",
    icon: FileCheck2,
    accent: "bridge",
    description:
      "Keep registrations, statutory documentation, permits, license renewals and recurring filings moving with a clear operational owner. We help identify obligations, prepare document packs, coordinate submissions with relevant agencies and track responses. Final approvals remain with the relevant regulator.",
    bullets: [
      "CAC business registration, post-incorporation updates and annual returns coordination",
      "PENCOM employer registration, pension documentation and compliance schedule support",
      "NSITF registration, contribution records and compliance documentation",
      "ITF registration, levy documentation and remittance support",
      "Mining and Export License renewals: We manage the renewal process for mining and mineral export licenses, helping businesses maintain regulatory compliance and avoid unnecessary interruptions to their operations.",
      "Tax clearance, statutory letters, permits and other agency filing coordination",
      "Compliance calendars, evidence packs and status reporting for your records",
    ],
    workflow: [
      "We understand your entity, activities and applicable obligations",
      "Our team prepares the required documents and coordinates submissions",
      "We track acknowledgements, queries, renewals and outstanding actions",
      "You receive a clean record of completed work and next due dates",
    ],
  },
];

export const getService = (slug) =>
  services.find((service) => service.slug === slug);

export const serviceGroups = [
  {
    title: "Move",
    description: "Vehicles and goods, coordinated across borders.",
    services: services.slice(0, 3),
  },
  {
    title: "Manage",
    description: "Local execution for people, property and projects.",
    services: services.slice(3, 5),
  },
  {
    title: "Grow",
    description: "Procurement and compliance for organisations.",
    services: services.slice(5),
  },
];
